import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Address2ComponentValue, SavePartyChangedField } from '@portal-workspace/grow-shared-library';

/** The editable company block, as the Key Company Details form hands it back. */
export interface KybCompanyDetailsValue {
  annualRevenue: number | null;
  businessPhone: string | null;
  businessContactEmail: string | null;
  principalPlaceOfBusinessAddress: Address2ComponentValue;
}

const text = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * Email check aligned with the save-application-parties API, which validates with validator.js
 * isEmail() defaults: Angular's Validators.email accepts addresses with no proper TLD ('aaa@a',
 * 'aaa@a.a') that the API then rejects with a 400, so the form must hold the field to the same
 * bar. Reports under the 'email' key so the existing mat-error shows. Empty is valid - the field
 * is optional and saves as null.
 */
export const kybBusinessEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = text(control.value);
  if (!value) {
    return null;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@.]{2,}$/.test(value) ? null : { email: true };
};

/**
 * Whether the email itself was edited, normalised the same way as kybCompanyDetailsChanged. Drives
 * the two email-specific save behaviours: an unchanged email is omitted from the save payload (the
 * API reads an absent email as "untouched"), and an email already invalid as loaded does not block
 * saving the fields that were edited.
 */
export const kybCompanyDetailsEmailChanged = (
  pristine: KybCompanyDetailsValue | undefined,
  current: KybCompanyDetailsValue,
): boolean => text(pristine?.businessContactEmail) !== text(current.businessContactEmail);

/**
 * The address parts a reviewer can edit, normalised for comparison.
 *
 * The `address` line is deliberately excluded: custom-address derives it from the parts and formats it
 * its own way, so it never matches the stored value it was loaded with and comparing it would report
 * an edit on every entity. Case and spacing are normalised for the same reason.
 */
const addressParts = (value: Address2ComponentValue): string =>
  [
    value?.UnitNumber,
    value?.StreetNumber,
    value?.StreetName,
    value?.StreetType,
    value?.Suburb,
    value?.State,
    value?.Postcode,
  ]
    .map(part => text(part).toUpperCase())
    .join('|');

/**
 * Whether the reviewer actually edited the company block, which decides whether this save reissues
 * privacy consent to every signatory.
 *
 * Deliberately not FormGroup.dirty. The annual revenue and address controls push a value out through
 * their value accessors as they initialise, which marks the group dirty before anything has been
 * touched, so clicking Edit alone would have armed a reissue for every signatory.
 */
/**
 * Whether the reviewer actually edited the company block, which decides whether this save reissues
 * privacy consent to every signatory.
 *
 * Deliberately not FormGroup.dirty. The annual revenue and address controls push a value out through
 * their value accessors as they initialise, which marks the group dirty before anything has been
 * touched, so clicking Edit alone would have armed a reissue for every signatory.
 */
export const kybCompanyDetailsChanged = (
  pristine: KybCompanyDetailsValue,
  current: KybCompanyDetailsValue,
): boolean => kybCompanyDetailsChangedFields(pristine, current).length > 0;

/**
 * The company-block fields the reviewer actually edited, as change keys of KybCompanyDetailsValue.
 * Drives the field list on the entity's audit row; the save payload itself always carries the whole
 * block, so the server cannot infer this from it.
 */
export const kybCompanyDetailsChangedFields = (
  pristine: KybCompanyDetailsValue,
  current: KybCompanyDetailsValue,
): string[] => {
  const changed: string[] = [];
  // Free-text inputs report exactly what the reviewer typed, so they compare directly - clearing one
  // is a real edit.
  if (text(pristine.businessPhone) !== text(current.businessPhone)) {
    changed.push('businessPhone');
  }
  if (text(pristine.businessContactEmail) !== text(current.businessContactEmail)) {
    changed.push('businessContactEmail');
  }
  // annual-revenue and custom-address report null when they cannot represent the stored value (a
  // revenue outside their options, an address missing parts they require). That is their verdict on
  // the data, not the reviewer clearing the field, so only a populated value counts as an edit.
  if (current.annualRevenue != null && current.annualRevenue !== pristine.annualRevenue) {
    changed.push('annualRevenue');
  }
  if (!!current.principalPlaceOfBusinessAddress
    && addressParts(current.principalPlaceOfBusinessAddress) !== addressParts(pristine.principalPlaceOfBusinessAddress)) {
    changed.push('principalPlaceOfBusinessAddress');
  }
  return changed;
};

/**
 * The edited company-block fields as audit-ready old/new entries, one per field. Built on the same
 * per-field verdicts as kybCompanyDetailsChangedFields so the audit row can never disagree with the
 * dirty check.
 */
export const kybCompanyDetailsChangedEntries = (
  pristine: KybCompanyDetailsValue,
  current: KybCompanyDetailsValue,
): SavePartyChangedField[] => {
  const revenue = (value: number | null | undefined): string =>
    value == null ? '' : `$${Number(value).toLocaleString('en-AU')}`;
  const addressLine = (value: Address2ComponentValue): string => (value?.address ?? '').trim();

  const displays: { [key: string]: (value: KybCompanyDetailsValue) => string } = {
    businessPhone: v => text(v.businessPhone),
    businessContactEmail: v => text(v.businessContactEmail),
    annualRevenue: v => revenue(v.annualRevenue),
    principalPlaceOfBusinessAddress: v => addressLine(v.principalPlaceOfBusinessAddress),
  };
  return kybCompanyDetailsChangedFields(pristine, current).map(field => ({
    field,
    oldValue: displays[field](pristine),
    newValue: displays[field](current),
  }));
};
